import { FetchResult, MutationResult, useMutation } from '@apollo/client';

import {
  evictCache,
  PeriodName,
  SET_ASC_PERIOD_LOCK,
  SetAscPeriodLockMutation,
  SetAscPeriodLockMutationVariables,
} from '@neo.tax/shared';

type SetAscPeriodLockParams = {
  fiscalYearId: string;
  periodName: PeriodName;
  isLocked: boolean;
};

type UseSetAscPeriodLock = [
  (params: SetAscPeriodLockParams) => Promise<FetchResult<SetAscPeriodLockMutation>>,
  MutationResult<SetAscPeriodLockMutation>,
];

export const useSetAscPeriodLock = (): UseSetAscPeriodLock => {
  const [setAscPeriodLockMutation, setAscPeriodLockMutationResult] = useMutation<
    SetAscPeriodLockMutation,
    SetAscPeriodLockMutationVariables
  >(SET_ASC_PERIOD_LOCK, {
    context: {
      errorToast: {
        title: 'Error updating period lock',
        description: 'Something unexpected prevented the change from saving. Please try again.',
      },
    },
    // Locking a period changes what the Projects and Cost pages are allowed to show, and the
    // month-level states of every period that contains this one.
    //
    // The status is normalized on (fiscalYearId, periodName), so the response updates the period
    // that was toggled — but every other period's state is derived from the same month rows, and
    // none of them are in the response. Evicting the field is what refetches those.
    update: (cache) => {
      evictCache(cache, ['getAscPeriodLockStatus']);
    },
  });

  const setAscPeriodLock = ({
    fiscalYearId,
    isLocked,
    periodName,
  }: SetAscPeriodLockParams): Promise<FetchResult<SetAscPeriodLockMutation>> =>
    setAscPeriodLockMutation({
      variables: { fiscalYearId, periodName, isLocked },
      context: {
        successToast: {
          title: isLocked ? 'Period locked' : 'Period unlocked',
        },
      },
    });

  return [setAscPeriodLock, setAscPeriodLockMutationResult];
};
